<template>
  <div>
    <b-card-actions
      :show-loading="saving || fetching"
      action-collapse
      action-refresh
      :title="$t('Lançamentos da Conciliação')"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="reconciliation-items-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('RECONCILIATION_ITEM.NAME'), 'male')"
        :fields="fields"
        :items="reconciliationItems"
        :tbody-tr-class="rowClass"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #thead-top>
          <b-tr>
            <b-th
              variant="primary"
              colspan="6"
            >
              {{ $t('Transação Stripe') }}
            </b-th>
            <b-th
              variant="primary"
              colspan="5"
            >
              {{ $t('Transação ERP') }}
            </b-th>
          </b-tr>
        </template>
        <!-- <template #cell(transactionDate)="row">
          <span :title="$options.filters.datetime(row.item.transactionDate)">{{
            row.item.transactionDate | time('-', true)
          }}</span>
        </template> -->
        <template #cell(receivableInstallmentNsu)="row">
          <span
            v-if="!row.item.receivableInstallments || !row.item.receivableInstallments.length"
          >-</span>
          <div
            v-for="(receivableInstallment, index) in row.item.receivableInstallments"
            :key="`receivableNsu-${row.item.id}-${index}`"
          >
            {{ receivableInstallment.receivable.nsu }}
          </div>
        </template>
        <template #cell(receivableInstallmentSaleId)="row">
          <span
            v-if="!row.item.receivableInstallments || !row.item.receivableInstallments.length"
          >-</span>
          <div
            v-for="(receivableInstallment, index) in row.item.receivableInstallments"
            :key="`receivableSaleId-${row.item.id}-${index}`"
          >
            {{ receivableInstallment.receivable.saleId }}
          </div>
        </template>
        <template #cell(receivableInstallmentValue)="row">
          <span
            v-if="!row.item.receivableInstallments || !row.item.receivableInstallments.length"
          >-</span>
          <div
            v-for="(receivableInstallment, index) in row.item.receivableInstallments"
            :key="`receivableSaleVale-${row.item.id}-${index}`"
          >
            {{ receivableInstallment.value | currency }}
          </div>
        </template>
        <template #cell(receivableDate)="row">
          <span
            v-if="!row.item.receivableInstallments || !row.item.receivableInstallments.length"
          >-</span>
          <div
            v-for="(receivableInstallment, index) in row.item.receivableInstallments"
            :key="`receivableDate-${row.item.id}-${index}`"
            :title="$options.filters.datetime(receivableInstallment.receivable.createdDate)"
          >
            {{ receivableInstallment.receivable.createdDate | datetime }}
          </div>
        </template>

        <template #cell(saleId)="row">
          <e-router-link
            v-if="row.item.receivableInstallment"
            :title="$t('Visualizar venda')"
            :to="{
              name: 'order-read-only',
              params: { id: row.item.receivableInstallment.receivable.saleId },
            }"
            :value="row.item.receivableInstallment.receivable.saleId"
          />
        </template>

        <template #cell(receivablePaymentMethodId)="row">
          <span
            v-if="!row.item.receivableInstallments || !row.item.receivableInstallments.length"
          >-</span>
          <FormulateInput
            v-else
            v-model="row.item.receivableInstallments[0].receivable.paymentMethodId"
            label-container-class="d-none"
            :options="paymentMethodsOptions"
            type="label"
          />
        </template>

        <template #custom-foot>
          <tr>
            <th colspan="3" />
            <th class="text-right">
              {{ stripeTotalValue | currency }}
            </th>
            <th class="text-right">
              {{ stripeTotalCommission | currency }}
            </th>
            <th class="text-right">
              {{ stripeTotal | currency }}
            </th>
            <th colspan="2" />
            <th class="text-right">
              {{ erpTotalValue | currency }}
            </th>
            <th colspan="3" />
          </tr>
        </template>
      </b-table>

      <b-row class="pt-1">
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="reconciliation-items-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable, BTr, BTh } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import { reconciliationDomains } from '@/mixins'
import {
  EPageSizeSelector,
  EPaginationSummary,
} from '@/views/components'
import ERouterLink from '@/views/components/ERouterLink.vue'

export default {
  components: {
    BRow,
    BCol,
    BPagination,
    BTable,
    BTr,
    BTh,
    BCardActions,
    EPageSizeSelector,
    EPaginationSummary,
    ERouterLink,
  },

  mixins: [reconciliationDomains],

  props: {
    saving: {
      type: Boolean,
      default: false,
    },
    fetching: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState('pages/financial/reconciliation/stripe/maintain', [
      'reconciliationItems',
      'paging',
      'sorting',
      'filters',
      'summaryFields',
    ]),

    ...mapGetters('app', ['paymentMethodsOptions']),
    ...mapGetters('pages/financial/reconciliation/stripe', ['acquirerProductsOptions']),

    stripeTotalValue() {
      return this.summaryFields?.stripeTotalValue?.value
    },
    stripeTotalCommission() {
      return this.summaryFields?.stripeTotalCommission?.value
    },
    stripeTotal() {
      return this.summaryFields?.stripeTotal?.value
    },
    erpTotalValue() {
      return this.summaryFields?.erpTotalValue?.value
    },

    fields() {
      return [
        {
          label: this.$t('Nsu'),
          key: 'paymentMethodId',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Transação'),
          key: 'paymentIntentId',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Cobrança'),
          key: 'chargeId',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Valor'),
          key: 'amount',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
          formatter: value => this.$options.filters.currency(value),
          sortable: true,
        },
        {
          label: this.$t('Comissão'),
          key: 'fee',
          tdClass: 'text-right',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.currency(value),
          sortable: true,
        },
        {
          label: this.$t('Liquido'),
          key: 'net',
          tdClass: 'text-right',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.currency(value),
          sortable: true,
        },
        {
          label: this.$t('Nsu'),
          key: 'receivableInstallmentNsu',
          tdClass: 'text-left',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Venda'),
          key: 'receivableInstallmentSaleId',
          sortKey: 'receivableInstallment.receivable.saleId',
          tdClass: 'text-left',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Valor'),
          key: 'receivableInstallmentValue',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Forma'),
          key: 'receivablePaymentMethodId',
          sortKey: 'receivableInstallment.receivable.paymentMethodId',
          tdClass: 'text-left',
          thStyle: { minWidth: '130px', maxWidth: '150px' },
        },
        {
          label: this.$t('Data'),
          key: 'receivableDate',
          sortKey: 'receivableInstallment.createdDate',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        }
      ]
    },
    buttons() {
      return item => {
        const buttons = []
        switch (item.status) {
          default:
            break
        }
        return buttons
      }
    },
  },

  mounted() {
    this.fetchAcquirerProducts()
  },

  methods: {
    ...mapActions('pages/financial/reconciliation/stripe/maintain', [
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),
    ...mapActions('pages/financial/reconciliation/stripe', ['fetchAcquirerProducts']),

    getData() {
      this.$emit('get-data')
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      if (item.receivableInstallments && item.receivableInstallments.length > 1) {
        return 'table-success'
      }
      return ''
    },
  },
}
</script>

<style></style>
